import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Popover,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ContentCopy, Close, OpenInFull, CloseFullscreen } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { MdAttachFile, MdClose } from 'react-icons/md';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Get, Post } from '../../../Axios/AxiosFunctions';
import { apiHeader, apiUrl, BaseURL } from '../../../config/apiUrl';
import Loader from '../../../Component/Loader';
import useDebounce from '../../../CustomHooks/useDebounce';
import { withSignature, getSignaturePhotoUrl } from '../../../utils/emailUtils';
import { outlookAuth } from '../../../utils/outlook';

const validationSchema = yup.object().shape({
  subject: yup.string().required('Subject is required'),
  content: yup.string().required('Content is required'),
});

export default function SendEmailDialog({ open, onClose, contact, initialSubject, initialContent, onSaveDraft, draftId }) {
  const {
    user,
    access_token: accessToken,
    company_id: companyId,
    outlookAuthenticated: isOutlookAuthenticated,
  } = useSelector((state) => state?.authReducer || {});
  console.log(user)
  const { frontendDomain } = useSelector((state) => state?.companyReducer);

  const [loading, setLoading] = useState(false);
  const [outlookName, setOutlookName] = useState('');
  const [outlookEmail, setOutlookEmail] = useState('');

  // CC/BCC states
  const [ccValue, setCcValue] = useState([]);
  const [bccValue, setBccValue] = useState([]);
  const [ccSearch, setCcSearch] = useState('');
  const [bccSearch, setBccSearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const debouncedCcSearch = useDebounce(ccSearch, 500);
  const debouncedBccSearch = useDebounce(bccSearch, 500);

  useEffect(() => {
    if (isOutlookAuthenticated && user?._id) {
      fetch(`${apiUrl}/outlook/profile`, outlookAuth(accessToken))
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data) {
            setOutlookName(data.displayName);
            setOutlookEmail(data.email);
          }
        })
        .catch(() => {});
    }
  }, [isOutlookAuthenticated, user?._id]);

  // Set default BCC to current user when dialog opens
  useEffect(() => {
    if (open && user?.email) {
      setBccValue([{ email: user.email, firstName: user.firstName, lastName: user.lastName }]);
    }
    if (!open) {
      // Reset CC/BCC when dialog closes
      setCcValue([]);
      setBccValue([]);
      setCcSearch('');
      setBccSearch('');
    }
  }, [open, user]);

  // Search contacts for CC/BCC autocomplete
  const searchContacts = async (searchTerm) => {
    if (!searchTerm) {
      setContacts([]);
      return;
    }
    setContactLoading(true);
    try {
      const response = await Get(
        BaseURL(`communication/s-contacts-conversion?search=${searchTerm}`),
        accessToken,
        companyId,
      );
      if (Array.isArray(response?.data?.result)) {
        setContacts(response?.data?.result);
      }
    } catch (error) {
      console.error('Error searching contacts:', error);
    } finally {
      setContactLoading(false);
    }
  };

  useEffect(() => {
    searchContacts(debouncedCcSearch || debouncedBccSearch);
  }, [debouncedCcSearch, debouncedBccSearch]);

  const handleCcBccClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCcBccClose = () => {
    setAnchorEl(null);
  };

  const ccBccOpen = Boolean(anchorEl);
  const ccBccId = ccBccOpen ? 'cc-bcc-popover' : undefined;

  const getContent = (content) => {
    const loginLink = `https://${frontendDomain}/login`;
    const resolved = content?.replaceAll(
      '*|login_link|*',
      `<a style="font-style:normal; text-decoration: normal;" href="${loginLink}" target="_blank">${loginLink}</a>`,
    );
    return withSignature(resolved, user, getSignaturePhotoUrl(user));
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      subject: initialSubject || '',
      content: initialContent || '',
      files: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      const { subject, content, files } = values;
      setLoading(true);

      const toEmail = contact?.email;
      if (!toEmail) {
        toast.error('No email address for this contact');
        setLoading(false);
        return;
      }

      // Outlook send logic
      if (isOutlookAuthenticated && user?._id) {
        try {
          const getBase64 = (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                resolve({
                  name: file.name,
                  contentBytes: base64,
                  contentType: file.type,
                });
              };
              reader.onerror = (error) => reject(error);
            });

          let attachments = [];
          if (files && files.length > 0) {
            attachments = await Promise.all(files.map(getBase64));
          }

          // Extract CC/BCC emails
          const ccEmails = ccValue.map((v) => v?.email).filter(Boolean);
          const bccEmails = bccValue.map((v) => v?.email).filter(Boolean);

          const payload = {
            userId: user._id,
            to: [toEmail],
            cc: ccEmails,
            bcc: bccEmails,
            subject,
            body: getContent(content),
            attachments,
            companyId,
            from: outlookEmail || user?.email,
          };

          const response = await Post(`${apiUrl}/outlook/send-email`, payload, apiHeader(accessToken));

          if (response?.data?.success) {
            toast.success('Email sent via Outlook!');
            formik.resetForm();
            onClose();
          } else {
            toast.error('Failed to send via Outlook.');
          }
        } catch (err) {
          toast.error(err?.response?.data?.error || err.toString());
        } finally {
          setLoading(false);
        }
        return;
      }

      // SendGrid fallback - extract CC/BCC emails
      const ccEmails = ccValue.map((v) => v?.email).filter(Boolean);
      const bccEmails = bccValue.map((v) => v?.email).filter(Boolean);

      const fromEmail = user?.email || user?.imap?.user || '';
      const fromName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

      const formData = new FormData();
      formData.append('from', fromEmail);
      formData.append('name', fromName);
      formData.append('to', toEmail);
      formData.append('cc', ccEmails.join(','));
      formData.append('bcc', bccEmails.join(','));
      formData.append('subject', subject);
      formData.append('emailContent', getContent(content));

      files?.forEach((file) => {
        formData.append('files', file);
      });

      try {
        const res = await Post(
          BaseURL('sendgrid/s-send-email'),
          formData,
          apiHeader(accessToken, true),
          companyId,
        );

        if (typeof res.data === 'string') {
          toast.error(res.data);
        } else {
          formik.resetForm();
          onClose();
          toast.success('Email sent successfully!');
        }
      } catch (err) {
        toast.error(err?.response?.data || err.toString());
      } finally {
        setLoading(false);
      }
    },
  });

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles?.length) {
        const files = formik.values.files?.length
          ? [...formik.values.files, ...acceptedFiles]
          : acceptedFiles;
        formik.setFieldValue('files', files);
      }
    },
    [formik.values.files],
  );

  const { getRootProps, getInputProps } = useDropzone({
    multiple: true,
    onDrop,
  });

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied!', { autoClose: 1500 });
    });
  };

  const [maximized, setMaximized] = useState(false);
  const [dialogSize, setDialogSize] = useState({ width: 720, height: 600 });
  const isResizing = useRef(false);
  const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 });

  // Reset maximized state when dialog opens
  useEffect(() => {
    if (!open) {
      setMaximized(false);
      setDialogSize({ width: 720, height: 600 });
    }
  }, [open]);

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      w: dialogSize.width,
      h: dialogSize.height,
    };

    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      const newWidth = Math.max(500, Math.min(window.innerWidth - 40, startPos.current.w + (e.clientX - startPos.current.x)));
      const newHeight = Math.max(400, Math.min(window.innerHeight - 40, startPos.current.h + (e.clientY - startPos.current.y)));
      setDialogSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [dialogSize]);

  const contactName = `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || 'Unknown';
  const fromName = isOutlookAuthenticated ? outlookName : `${user?.firstName} ${user?.lastName}`;
  const fromEmail = isOutlookAuthenticated ? outlookEmail : user?.email;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ zIndex: 1300 }}
      PaperProps={{
        sx: maximized
          ? {
              width: '95vw',
              height: '95vh',
              maxWidth: '95vw',
              maxHeight: '95vh',
              m: 0,
              transition: 'all 0.25s ease-in-out',
            }
          : {
              width: dialogSize.width,
              height: dialogSize.height,
              maxWidth: '95vw',
              maxHeight: '95vh',
              m: 0,
              transition: isResizing.current ? 'none' : 'all 0.25s ease-in-out',
              position: 'relative',
            },
      }}
    >
      <DialogTitle sx={{ py: 1.5, pr: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Send Email to {contactName}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={maximized ? 'Restore' : 'Maximize'}>
              <IconButton onClick={() => setMaximized((prev) => !prev)} size="small">
                {maximized ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'hidden', pb: 1, pt: '6px !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {loading && <Loader />}
        <Stack spacing={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Grid container sx={{ paddingTop: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="From Name"
                fullWidth
                size="small"
                value={fromName}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton size="small" onClick={() => handleCopy(fromName)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="From Email"
                fullWidth
                size="small"
                value={fromEmail}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton size="small" onClick={() => handleCopy(fromEmail)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <TextField
            label="To Email"
            fullWidth
            size="small"
            value={contact?.email || ''}
            InputProps={{ readOnly: true }}
          />

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Subject"
              name="subject"
              fullWidth
              size="small"
              value={formik.values.subject}
              onChange={formik.handleChange}
              error={formik.touched.subject && !!formik.errors.subject}
              helperText={formik.touched.subject && formik.errors.subject}
            />
            <Button
              aria-describedby={ccBccId}
              variant="contained"
              onClick={handleCcBccClick}
              sx={{ minWidth: 90, height: 40 }}
            >
              CC/BCC
            </Button>
          </Stack>

          {/* CC/BCC Display */}
          {(ccValue.length > 0 || bccValue.length > 0) && (
            <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '12px' }}>
              {ccValue.length > 0 && (
                <Typography variant="caption" display="block">
                  <strong>CC:</strong> {ccValue.map((v) => v.email).join(', ')}
                </Typography>
              )}
              {bccValue.length > 0 && (
                <Typography variant="caption" display="block">
                  <strong>BCC:</strong> {bccValue.map((v) => v.email).join(', ')}
                </Typography>
              )}
            </Box>
          )}

          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              flex: 1,
              minHeight: 150,
              display: 'flex',
              flexDirection: 'column',
              '& .ck.ck-editor': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                flex: 1,
                border: 'none',
              },
              '& .ck.ck-editor__top': {
                borderBottom: '1px solid #ccc',
              },
              '& .ck.ck-editor__main': {
                flexGrow: 1,
                overflow: 'hidden',
              },
              '& .ck-editor__editable': {
                height: '100% !important',
                maxHeight: 'none !important',
                overflow: 'auto !important',
                border: '1px solid #ccc !important',
                borderTop: 'none !important',
              },
            }}
          >
            <CKEditor
              editor={ClassicEditor}
              data={formik.values.content}
              onChange={(_, editor) => {
                formik.setFieldValue('content', editor.getData());
              }}
            />
          </Box>
          {formik.touched.content && formik.errors.content && (
            <Typography color="error" fontSize={12}>
              {formik.errors.content}
            </Typography>
          )}

          {formik.values.files?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formik.values.files.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid gray',
                    padding: '2px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                  }}
                >
                  <Typography variant="body2">{file.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newFiles = [...formik.values.files];
                      newFiles.splice(index, 1);
                      formik.setFieldValue('files', newFiles);
                    }}
                  >
                    <MdClose size={16} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box
            {...getRootProps()}
            sx={{
              cursor: 'pointer',
              border: '1px dashed #1976D2',
              borderRadius: 1,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <input {...getInputProps()} />
            <MdAttachFile size={20} color="#1976D2" />
            <Typography color="primary" variant="body2">
              Attach Files (click or drag)
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {onSaveDraft && (
          <Button
            variant="outlined"
            color="secondary"
            disabled={loading}
            onClick={() => {
              onSaveDraft({
                toEmail: contact?.email || '',
                toName: `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim(),
                subject: formik.values.subject,
                body: formik.values.content,
                cc: ccValue.map((v) => v?.email).filter(Boolean),
                bcc: bccValue.map((v) => v?.email).filter(Boolean),
              });
            }}
          >
            {draftId ? 'Update Draft' : 'Save to Draft'}
          </Button>
        )}
        <Button onClick={formik.submitForm} variant="contained" disabled={loading}>
          Send
        </Button>
      </DialogActions>

      {/* Resize handle (bottom-right corner) */}
      {!maximized && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 20,
            height: 20,
            cursor: 'nwse-resize',
            zIndex: 10,
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 4,
              right: 4,
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '0 0 10px 10px',
              borderColor: 'transparent transparent #bbb transparent',
            },
          }}
        />
      )}

      {/* CC/BCC Popover */}
      <Popover
        id={ccBccId}
        open={ccBccOpen}
        anchorEl={anchorEl}
        onClose={handleCcBccClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 350 }}>
          <Typography variant="subtitle2" gutterBottom>
            CC Email
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            limitTags={2}
            id="cc-email"
            options={contacts}
            value={ccValue}
            inputValue={ccSearch}
            onInputChange={(e, newValue) => setCcSearch(newValue)}
            onChange={(e, newValue) => {
              // Handle both contact objects and free text (typed emails)
              const processed = newValue.map((item) => {
                if (typeof item === 'string') {
                  return { email: item, firstName: '', lastName: '' };
                }
                return item;
              });
              setCcValue(processed);
            }}
            loading={contactLoading}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.email
                ? `${option.email} (${option.firstName || ''} ${option.lastName || ''})`
                : '';
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type email or search contacts"
                size="small"
              />
            )}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" gutterBottom>
            BCC Email
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            limitTags={2}
            id="bcc-email"
            options={contacts}
            value={bccValue}
            inputValue={bccSearch}
            onInputChange={(e, newValue) => setBccSearch(newValue)}
            onChange={(e, newValue) => {
              const processed = newValue.map((item) => {
                if (typeof item === 'string') {
                  return { email: item, firstName: '', lastName: '' };
                }
                return item;
              });
              setBccValue(processed);
            }}
            loading={contactLoading}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.email
                ? `${option.email} (${option.firstName || ''} ${option.lastName || ''})`
                : '';
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type email or search contacts"
                size="small"
              />
            )}
          />

          <Button
            variant="outlined"
            size="small"
            onClick={handleCcBccClose}
            sx={{ mt: 2 }}
          >
            Done
          </Button>
        </Box>
      </Popover>
    </Dialog>
  );
}
